import { useCallback, useEffect, useMemo, useState } from "react";
import { ChefHat, Check, Plus, Save, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { field, label } from "./IngredientEditor";
import { fmtQty, invApi } from "./api";

export function RecipeMappingTab({ token }) {
  const [menu, setMenu] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState({});
  const [selected, setSelected] = useState("");
  const [lines, setLines] = useState([]);
  const [q, setQ] = useState("");
  const [addId, setAddId] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [m, ing, rec] = await Promise.all([api.get("/menu"), invApi(token).get("/inventory/ingredients"), invApi(token).get("/inventory/recipes")]);
      setMenu(m.data);
      setIngredients(ing.data);
      setRecipes(rec.data);
    } catch { toast.error("Couldn't load recipes"); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setLines((recipes[selected] || []).map((l) => ({ ingredient_id: l.ingredient_id, quantity: String(l.quantity) })));
    setAddId("");
  }, [selected, recipes]);

  const ingById = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);
  const grouped = useMemo(() => {
    const out = {};
    menu.filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase())).forEach((m) => { (out[m.category] ||= []).push(m); });
    return out;
  }, [menu, q]);
  const current = menu.find((m) => m.id === selected);
  const available = ingredients.filter((i) => !lines.some((l) => l.ingredient_id === i.id));
  const dirty = JSON.stringify(lines.map((l) => [l.ingredient_id, parseFloat(l.quantity) || 0])) !== JSON.stringify((recipes[selected] || []).map((l) => [l.ingredient_id, l.quantity]));

  const save = async () => {
    const body = lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: parseFloat(l.quantity) }));
    if (body.some((l) => !(l.quantity > 0))) return toast.error("Every ingredient needs a portion greater than 0");
    setSaving(true);
    try {
      await invApi(token).put(`/inventory/recipes/${selected}`, { lines: body });
      setRecipes((r) => ({ ...r, [selected]: body }));
      toast.success(`Recipe saved for ${current?.name}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save recipe"); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove the whole recipe for ${current?.name}? Sales will stop deducting for it.`)) return;
    try {
      await invApi(token).put(`/inventory/recipes/${selected}`, { lines: [] });
      setRecipes((r) => { const n = { ...r }; delete n[selected]; return n; });
      toast.success("Recipe removed");
    } catch { toast.error("Couldn't remove recipe"); }
  };

  const cost = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (ingById[l.ingredient_id]?.cost_per_unit || 0), 0);

  if (loading) return <div className="text-center text-stone-400 py-10 text-sm">Loading…</div>;

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4" data-testid="recipe-mapping">
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden md:max-h-[75vh] flex flex-col">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
          <ChefHat className="w-4 h-4 text-brand-red" />
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Menu items</div>
          <span className="ml-auto text-[10px] text-stone-500 font-bold">{Object.keys(recipes).length}/{menu.length} mapped</span>
        </div>
        <div className="px-4 py-2 border-b border-stone-100 flex items-center gap-2">
          <Search className="w-4 h-4 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search menu…" data-testid="recipe-menu-search" className="flex-1 text-sm outline-none bg-transparent py-1" />
        </div>
        <div className="overflow-y-auto flex-1 max-h-[40vh] md:max-h-none">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-stone-400 font-black sticky top-0 bg-white">{cat}{cat === "Add-Ons" ? " · extras" : ""}</div>
              {items.map((m) => {
                const n = recipes[m.id]?.length || 0;
                return (
                  <button key={m.id} type="button" onClick={() => setSelected(m.id)} data-testid={`recipe-menu-item-${m.id}`}
                    className={`w-full text-left px-4 py-2 flex items-center gap-2 text-sm ${selected === m.id ? "bg-red-50 text-brand-red font-black" : "text-stone-700 hover:bg-stone-50"}`}>
                    <span className="flex-1 truncate">{m.name}</span>
                    {n > 0 ? <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 flex items-center gap-1"><Check className="w-3 h-3" />{n}</span>
                      : <span className="text-[10px] text-stone-300 font-bold">—</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden self-start">
        {!current ? (
          <div className="px-6 py-16 text-center text-stone-400 text-sm" data-testid="recipe-empty-state">Pick a menu item to map its ingredients and portion sizes.</div>
        ) : (
          <div data-testid="recipe-editor">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
              <div>
                <div className="font-black text-stone-900 text-base leading-tight" data-testid="recipe-selected-name">{current.name}</div>
                <div className="text-[11px] text-stone-500">{current.category} · {current.description}</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {recipes[selected]?.length > 0 && (
                  <button type="button" onClick={clear} data-testid="recipe-clear-btn" className="text-[10px] uppercase tracking-widest font-black text-stone-500 hover:text-red-700 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                )}
                <button type="button" onClick={save} disabled={!dirty || saving} data-testid="recipe-save-btn"
                  className="bg-brand-red text-white px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1 disabled:opacity-40"><Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}</button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {lines.length === 0 && <div className="text-sm text-stone-400 text-center py-4" data-testid="recipe-no-lines">No ingredients yet — add the first one below.</div>}
              {lines.map((l, idx) => {
                const ing = ingById[l.ingredient_id];
                return (
                  <div key={l.ingredient_id} className="flex items-center gap-2" data-testid={`recipe-line-${l.ingredient_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-stone-800 text-sm truncate">{ing?.name || "Deleted ingredient"}</div>
                      <div className="text-[11px] text-stone-400">On hand {fmtQty(ing?.current_stock)} {ing?.unit}</div>
                    </div>
                    <input value={l.quantity} onChange={(e) => setLines((ls) => ls.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x)))}
                      type="number" step="any" min="0" inputMode="decimal" data-testid={`recipe-qty-${l.ingredient_id}`}
                      className="w-24 px-3 py-2 rounded-xl border-2 border-stone-200 text-right font-black tabular-nums outline-none focus:border-brand-red" />
                    <span className="text-xs text-stone-500 font-bold w-10">{ing?.unit}</span>
                    <button type="button" onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))} data-testid={`recipe-remove-${l.ingredient_id}`} className="p-1.5 text-stone-400 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
              <div className="border-t border-stone-100 pt-3">
                <span className={label}>Add ingredient</span>
                <div className="flex gap-2 mt-1">
                  <select value={addId} onChange={(e) => setAddId(e.target.value)} className={field} data-testid="recipe-add-select">
                    <option value="">Choose…</option>
                    {available.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
                  </select>
                  <button type="button" disabled={!addId} data-testid="recipe-add-btn"
                    onClick={() => { setLines((ls) => [...ls, { ingredient_id: addId, quantity: "1" }]); setAddId(""); }}
                    className="shrink-0 bg-stone-800 text-white px-4 rounded-xl text-xs uppercase tracking-widest font-black disabled:opacity-40 flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
                </div>
                {ingredients.length === 0 && <div className="text-[11px] text-amber-700 mt-2">No ingredients registered yet — add some from the Inventory tab first.</div>}
              </div>
              {cost > 0 && <div className="text-[11px] text-stone-500 text-right" data-testid="recipe-cost">Est. ingredient cost per item: <b>${cost.toFixed(2)}</b></div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
